import type { ComponentProps } from 'react';

import Link from '@vs-shop/image-link/Link';

import { clsxm } from '@vs-shop/utils';

import type { VsLinkProps } from '@vs-shop/headless/vsLink';

import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * What a glossary index shows once a letter or a query has resolved: a run of
 * terms, either flowing down newspaper columns or gathered under the initial
 * they share. Flat named parts a tenant arranges in its own JSX, each painting
 * itself.
 *
 *   <GlossaryIndex>
 *     <GlossaryIndexCount>{countLine}</GlossaryIndexCount>
 *     <GlossaryIndexColumns>
 *       {groups.map(([letter, terms]) => (
 *         <GlossaryIndexGroup key={letter}>
 *           <GlossaryIndexGroupLetter>{letter}</GlossaryIndexGroupLetter>
 *           <GlossaryIndexGroupTerms>
 *             {terms.map((term) => (
 *               <GlossaryIndexTermItem key={term.code}>
 *                 <GlossaryIndexTerm href={term.href}>{term.name}</GlossaryIndexTerm>
 *               </GlossaryIndexTermItem>
 *             ))}
 *           </GlossaryIndexGroupTerms>
 *         </GlossaryIndexGroup>
 *       ))}
 *     </GlossaryIndexColumns>
 *   </GlossaryIndex>
 *
 * **The layout is CSS `columns`, not a grid**, and that is load-bearing: a
 * glossary is read down a column and across to the next, so the browser flows
 * the terms rather than the section placing them. Every part that can be broken
 * across a column boundary carries `break-inside-avoid`, and a composition that
 * puts an unmarked block in the flow will see it split mid-group at some width
 * with no class having changed.
 *
 * **The parts take resolved data.** Which terms these are, how they were
 * grouped, what the count line says and whether the answer is a listing, a
 * failure or nothing at all are all the tenant's — the atoms, the `AsyncResult`
 * match and the paging stay in the tenant package.
 *
 * Server-first: every part is a plain server component. A retry control inside
 * {@link GlossaryIndexNotice} is a tenant's own client leaf.
 */

/**
 * The block the run of terms sits in, centred and capped so the columns stay a
 * readable width on a wide page.
 */
const glossaryIndexClasses = 'flex w-full max-w-[1240px] flex-col items-center';

export type GlossaryIndexProps = ComponentProps<'section'>;

/** The index's block. */
export const GlossaryIndex = ({ className, ...props }: GlossaryIndexProps) => (
  <section
    data-slot="glossary-index"
    className={clsxm(glossaryIndexClasses, className)}
    {...props}
  />
);

/**
 * The line above the run: how many terms it holds, or what the run is.
 *
 * Not a heading. A glossary index is furniture a page places and the page's own
 * `<h1>` already names it; a count line contributing an `<h2>` of its own would
 * write the page's outline for it.
 */
const glossaryIndexCountClasses =
  'mb-8 inline font-heading text-2xl tracking-tight lap:text-[28px]';

export type GlossaryIndexCountProps = ComponentProps<'div'>;

/**
 * The line that puts a question to the shopper above the index — "choose a
 * letter", "what is X?".
 *
 * The element is the page's, like every title in this tier: a page composes
 * `@vs-shop/ui/heading` through `render` where its outline calls for one.
 */
export type GlossaryIndexPromptProps = ComponentProps<'div'> & {
  /** The element to paint instead of the neutral `<div>`. */
  readonly render?: RenderElement;
};

/** The index's question. */
export const GlossaryIndexPrompt = ({ className, render, ...props }: GlossaryIndexPromptProps) => (
  <RenderedPart
    dataSlot="glossary-index-prompt"
    className={clsxm('font-heading lap:text-[28px] inline text-2xl tracking-tight', className)}
    render={render}
    {...props}
  />
);

/** The line naming what the run is showing. */
export const GlossaryIndexCount = ({ className, ...props }: GlossaryIndexCountProps) => (
  <div
    data-slot="glossary-index-count"
    className={clsxm(glossaryIndexCountClasses, className)}
    {...props}
  />
);

/**
 * The column flow itself.
 *
 * Two columns on a phone and five on a laptop, with the gutter the only spacing
 * the section states — how tall the flow is follows from how many terms there
 * are, which is the point of a column layout.
 */
const glossaryIndexColumnsClasses = 'w-full columns-2 gap-x-8 tab:columns-3 lap:columns-5';

export type GlossaryIndexColumnsProps = ComponentProps<'div'> & {
  /**
   * The element to paint instead of the default `<div>`. A run whose items are
   * each a list entry composes `<ul />` here; a run of loose links, or of
   * groups that carry their own lists, keeps the neutral box.
   *
   * Declared by what this part reads off the element rather than by a tag, so
   * any element with a `className` qualifies.
   */
  readonly render?: RenderElement;
};

/** The columns the terms flow down. */
export const GlossaryIndexColumns = ({
  className,
  render,
  ...props
}: GlossaryIndexColumnsProps) => (
  <RenderedPart
    dataSlot="glossary-index-columns"
    className={clsxm(glossaryIndexColumnsClasses, className)}
    render={render}
    {...props}
  />
);

/**
 * One initial's group, kept whole.
 *
 * `break-inside-avoid` is what stops a letter's heading ending a column with
 * its terms starting the next one — the defect a class list cannot show,
 * because the group is identical either way and only the width decides.
 */
const glossaryIndexGroupClasses = 'mb-8 flex break-inside-avoid flex-col gap-2';

export type GlossaryIndexGroupProps = ComponentProps<'div'>;

/** A run of terms sharing an initial. */
export const GlossaryIndexGroup = ({ className, ...props }: GlossaryIndexGroupProps) => (
  <div
    data-slot="glossary-index-group"
    className={clsxm(glossaryIndexGroupClasses, className)}
    {...props}
  />
);

/** The initial a group is filed under. A neutral element, for the reason the count line is one. */
const glossaryIndexGroupLetterClasses =
  'mb-2 inline font-heading text-2xl tracking-tight lap:text-[28px]';

export type GlossaryIndexGroupLetterProps = ComponentProps<'div'>;

/** The group's initial. */
export const GlossaryIndexGroupLetter = ({
  className,
  ...props
}: GlossaryIndexGroupLetterProps) => (
  <div
    data-slot="glossary-index-group-letter"
    className={clsxm(glossaryIndexGroupLetterClasses, className)}
    {...props}
  />
);

const glossaryIndexGroupTermsClasses = 'flex flex-col gap-1';

export type GlossaryIndexGroupTermsProps = ComponentProps<'ul'>;

/** The terms under one initial. */
export const GlossaryIndexGroupTerms = ({ className, ...props }: GlossaryIndexGroupTermsProps) => (
  <ul
    data-slot="glossary-index-group-terms"
    className={clsxm(glossaryIndexGroupTermsClasses, className)}
    {...props}
  />
);

/**
 * One term's cell in a column flow.
 *
 * The step below it and its unbreakability are the cell's own: a term that
 * wraps to two lines must keep both in one column, and the rhythm of the run is
 * the cell's rather than the flow's.
 */
const glossaryIndexTermItemClasses = 'mb-2 break-inside-avoid';

export type GlossaryIndexTermItemProps = ComponentProps<'li'>;

/** One term's cell. */
export const GlossaryIndexTermItem = ({ className, ...props }: GlossaryIndexTermItemProps) => (
  <li
    data-slot="glossary-index-term-item"
    className={clsxm(glossaryIndexTermItemClasses, className)}
    {...props}
  />
);

/** The term, as the link into its page. */
const glossaryIndexTermClasses = 'block text-base text-foreground hover:text-primary';

export type GlossaryIndexTermProps = VsLinkProps;

/**
 * A term.
 *
 * `block` rather than inline: the whole line is the target, so a term whose
 * name wraps stays one click surface. Renders through the `Link` seam, so a
 * host that aliases the specifier keeps client-side navigation and one that
 * does not resolves a plain `<a>`.
 */
export const GlossaryIndexTerm = ({ className, ...props }: GlossaryIndexTermProps) => (
  <Link
    data-slot="glossary-index-term"
    className={clsxm(glossaryIndexTermClasses, className)}
    {...props}
  />
);

/**
 * One bar at the height of a line of terms, with the same step below it and the
 * same unbreakability {@link GlossaryIndexTermItem} carries — a bar always
 * stands in a column flow, so re-supplying that at every call site was three
 * copies of the same two classes.
 */
const glossaryIndexTermSkeletonClasses =
  'bg-foreground/10 mb-2 h-4 w-full animate-pulse rounded-xs break-inside-avoid';

export type GlossaryIndexTermSkeletonProps = ComponentProps<'div'>;

/**
 * A term that has not arrived yet — one bar at the height of a line of terms,
 * so the flow it stands in is the height the answer will be.
 *
 * `aria-hidden`, because a bar names nothing: a screen reader announcing thirty
 * of them says less than saying nothing.
 */
export const GlossaryIndexTermSkeleton = ({
  className,
  ...props
}: GlossaryIndexTermSkeletonProps) => (
  <div
    data-slot="glossary-index-term-skeleton"
    aria-hidden
    className={clsxm(glossaryIndexTermSkeletonClasses, className)}
    {...props}
  />
);

/**
 * The box an index shows instead of a run: a centred column with room for a
 * line of copy and a way out. Shared by {@link GlossaryIndexEmpty} and
 * {@link GlossaryIndexError}, which are the same box making different claims.
 */
const glossaryIndexNoticeClasses = 'flex flex-col items-center gap-4 py-8';

export type GlossaryIndexEmptyProps = ComponentProps<'div'>;

/**
 * What stands where the run would have been when nothing matched.
 *
 * A part of its own rather than a branch inside the flow: an absent run yields
 * no place, and the composition decides which of the two it renders. The way
 * out — clear the filter — is the tenant's control, in the tenant's words, and
 * it is composed as children.
 */
export const GlossaryIndexEmpty = ({ className, ...props }: GlossaryIndexEmptyProps) => (
  <div
    data-slot="glossary-index-empty"
    className={clsxm(glossaryIndexNoticeClasses, className)}
    {...props}
  />
);

export type GlossaryIndexErrorProps = ComponentProps<'div'>;

/**
 * What stands there when the read FAILED, which is not the same thing.
 *
 * A separate part rather than an appearance of the empty state: presenting a
 * failure as an empty result tells the shopper the catalogue is empty when the
 * backend is merely unreachable. `role="alert"` is the part's own — the claim a
 * screen reader acts on belongs to the part that IS the failure, not to a prop
 * a caller might forget.
 */
export const GlossaryIndexError = ({ className, ...props }: GlossaryIndexErrorProps) => (
  <div
    role="alert"
    data-slot="glossary-index-error"
    className={clsxm(glossaryIndexNoticeClasses, className)}
    {...props}
  />
);

const glossaryIndexNoticeMessageClasses = 'text-foreground-muted text-base';

export type GlossaryIndexNoticeMessageProps = ComponentProps<'p'>;

/** What an empty state or a failure says. */
export const GlossaryIndexNoticeMessage = ({
  className,
  ...props
}: GlossaryIndexNoticeMessageProps) => (
  <p
    data-slot="glossary-index-notice-message"
    className={clsxm(glossaryIndexNoticeMessageClasses, className)}
    {...props}
  />
);

/**
 * The quiet line under a search result explaining what the result is — that the
 * catalogue answered with its closest matches rather than with everything.
 *
 * Whether it is shown at all is the composition's: an absent part yields no
 * place, and a listing reached by letter has nothing to explain.
 */
const glossaryIndexHintClasses = 'text-foreground-muted text-center text-sm';

export type GlossaryIndexHintProps = ComponentProps<'p'>;

/** The search result's explanatory line. */
export const GlossaryIndexHint = ({ className, ...props }: GlossaryIndexHintProps) => (
  <p
    data-slot="glossary-index-hint"
    className={clsxm(glossaryIndexHintClasses, className)}
    {...props}
  />
);
